/**
 * Shared hook that wires up drag-and-drop file upload onto any DOM element.
 *
 * Returns:
 *   - dragHandlers — spread onto the drop-target element. Uses an enter/leave
 *     counter so `isDragging` doesn't flicker when the cursor crosses child
 *     boundaries inside the target.
 *   - isDragging  — true while a drag with at least one accepted file is
 *     hovering over the target.
 *
 * Filtering rules mirror the upload endpoint allowlist (PDF / PNG / JPEG).
 * Size (≤20 MB) is enforced server-side; the hook only filters by MIME
 * prefix so we don't waste a POST on, e.g., a dropped .txt file. PDFs whose
 * `type` is empty (some browsers / OS combos) fall back to a `.pdf`
 * extension check.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useChatFeedbackLock } from './ChatFeedbackOverlays';

const DEFAULT_ACCEPT = ['application/pdf', 'image/png', 'image/jpeg'];

interface DragDropOpts {
  /** Called with the filtered list of accepted files dropped on the target. */
  onFiles: (files: File[]) => void;
  /** MIME prefixes to accept. Defaults to PDF/PNG/JPEG. */
  accept?: string[];
  /** When true, all handlers are no-ops and `isDragging` stays false. */
  disabled?: boolean;
}

interface DragDropResult {
  dragHandlers: {
    onDragEnter: (e: React.DragEvent) => void;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
  };
  isDragging: boolean;
}

function hasAcceptedExtension(name: string): boolean {
  // Fallback for files where browser left .type empty (e.g. some PDFs).
  const lower = name.toLowerCase();
  return (
    lower.endsWith('.pdf') ||
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg')
  );
}

function filterAccepted(files: File[], accept: string[]): File[] {
  return files.filter((f) => {
    // Skip directories: browsers report them as items with empty type
    // and zero size. We can't always distinguish, but a 0-byte file with
    // no type and no usable extension is overwhelmingly a directory.
    if (!f.type) {
      return hasAcceptedExtension(f.name);
    }
    return accept.some((prefix) => f.type.startsWith(prefix));
  });
}

export function useDragDropUpload(opts: DragDropOpts): DragDropResult {
  const { onFiles, accept = DEFAULT_ACCEPT } = opts;
  const locked = useChatFeedbackLock();
  const disabled = Boolean(opts.disabled) || locked;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Counter pattern: dragenter increments, dragleave decrements. Without
  // this, dragging across child elements fires enter→leave→enter and the
  // overlay flickers. Keeping it in a ref (not state) avoids stale closures.
  const enterCount = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const reset = useCallback(() => {
    enterCount.current = 0;
    setIsDragging(false);
  }, []);

  // The gate intercepts drop before this hook can reset an active drag.
  useEffect(() => { if (disabled) reset(); }, [disabled, reset]);

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabledRef.current) { e.stopPropagation(); return; }
      enterCount.current += 1;
      // Only flip on first enter; subsequent enters (into nested children)
      // just bump the counter.
      if (enterCount.current === 1) setIsDragging(true);
    },
    [disabledRef]
  );

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabledRef.current) { e.preventDefault(); e.stopPropagation(); return; }
      // preventDefault on dragover is required to allow a drop on the target.
      e.preventDefault();
    },
    [disabledRef]
  );

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabledRef.current) { e.stopPropagation(); return; }
      enterCount.current = Math.max(0, enterCount.current - 1);
      if (enterCount.current === 0) setIsDragging(false);
    },
    [disabledRef]
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabledRef.current) { e.stopPropagation(); return; }
      reset();
      const dt = e.dataTransfer;
      const all: File[] = dt?.files ? Array.from(dt.files as any) : [];
      const accepted = filterAccepted(all, accept);
      if (accepted.length > 0) onFiles(accepted);
    },
    [onFiles, accept, reset, disabledRef]
  );

  return {
    dragHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
    isDragging,
  };
}

export default useDragDropUpload;
